// src/pages/admin/AdminOrderDetails.jsx
import React from "react";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

const AdminOrderDetails = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const API_URL = process.env.REACT_APP_API_URL;
  const token = Cookies.get("gameport");

  // Fetch Order Data
  const {
    data: order,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-order", id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/order/order/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to load order");
      return data;
    },
    enabled: Boolean(id),
  });

  // Update Status Mutation
  const { mutate: updateStatus, isPending: updating } = useMutation({
    mutationFn: async (newStatus) => {
      const res = await fetch(`${API_URL}/order/orders/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ orderStatus: newStatus }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to update order");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-order", id] });
      toast.success("Order updated successfully");
    },
    onError: (err) => toast.error(err.message || "Update failed"),
  });

  // Loading State
  if (isPending) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center space-y-3">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-medium text-gray-500">Loading order details...</p>
      </div>
    );
  }

  // Error State
  if (isError) {
    return (
      <div className="bg-red-50 rounded-2xl border border-red-100 p-6 flex flex-col items-center justify-center min-h-[30vh]">
        <span className="text-3xl mb-2">⚠️</span>
        <h3 className="text-red-800 font-semibold mb-1">Failed to load order</h3>
        <p className="text-red-600 text-sm text-center">{error?.message}</p>
      </div>
    );
  }

  const currentStatus = order?.orderStatus || "N/A";
  
  // Calculate total price dynamically from items
  const orderTotal = order?.items?.reduce((total, item) => {
    const price = item?.product?.finalPrice || 0;
    const qty = item?.quantity || 1;
    return total + (price * qty);
  }, 0);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      
      {/* 1. Header Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Order #{order?.orderCode || id.slice(-8).toUpperCase()}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage this order's details and delivery status.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-200 px-4 py-2">
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Status:</span>
          <span className={`text-sm font-bold capitalize ${
            currentStatus === 'delivered' ? 'text-green-600' :
            currentStatus === 'shipped' ? 'text-blue-600' :
            'text-yellow-600'
          }`}>
            {currentStatus}
          </span>
        </div>
      </div>

      {/* 2. Customer & Shipping Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Customer Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-50 pb-2">
            Customer Information
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Full Name</p>
              <p className="text-sm font-medium text-gray-900">{order?.user?.fullName || "Guest User"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Email Address</p>
              <p className="text-sm font-medium text-gray-900">{order?.user?.email || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Shipping Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-50 pb-2">
            Shipping Details
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Courier Service</p>
              <p className="text-sm font-medium text-gray-900">{order?.courier || "Not assigned"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tracking Link</p>
              {order?.trackingLink ? (
                <a
                  href={order.trackingLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 underline underline-offset-2 break-all"
                >
                  Track Package ↗
                </a>
              ) : (
                <p className="text-sm font-medium text-gray-900">No tracking link available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Order Items List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
            Ordered Items
          </h2>
        </div>
        
        <div className="divide-y divide-gray-100">
          {order?.items?.length > 0 ? (
            order.items.map((item) => (
              <div key={item._id} className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 hover:bg-gray-50 transition-colors">
                
                {/* Product Image */}
                <div className="w-20 h-20 shrink-0 bg-gray-100 rounded-xl overflow-hidden border border-gray-200">
                  {item.product?.images?.[0] ? (
                    <img
                      src={item.product.images[0]}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Img
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {item.product?.name || "Product no longer available"}
                  </h3>
                  <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                    <p>Unit Price: <span className="font-medium text-gray-700">${item.product?.finalPrice || "0.00"}</span></p>
                    <p>Quantity: <span className="font-medium text-gray-700">{item.quantity || 1}</span></p>
                  </div>
                </div>

                {/* Item Subtotal */}
                <div className="text-right sm:ml-auto">
                  <p className="text-xs text-gray-500 mb-1">Subtotal</p>
                  <p className="text-lg font-bold text-gray-900">
                    ${((item.product?.finalPrice || 0) * (item.quantity || 1)).toFixed(2)}
                  </p>
                </div>

              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              No items found in this order.
            </div>
          )}
        </div>

        {/* Order Total Footer */}
        <div className="px-6 py-5 bg-gray-50 border-t border-gray-100 flex justify-end">
          <div className="text-right">
            <p className="text-sm text-gray-500 font-medium">Total Amount</p>
            <p className="text-2xl font-bold text-gray-900">${orderTotal.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* 4. Admin Actions */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-50 pb-2">
          Update Order Status
        </h2>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={updating || currentStatus === "processing"}
            onClick={() => updateStatus("processing")}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all
              ${currentStatus === "processing" 
                ? "bg-yellow-100 text-yellow-800 border border-yellow-200 cursor-default" 
                : "bg-white text-gray-700 border border-gray-200 hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-200"
              } disabled:opacity-50`}
          >
            Mark as Processing
          </button>

          <button
            type="button"
            disabled={updating || currentStatus === "shipped"}
            onClick={() => updateStatus("shipped")}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all
              ${currentStatus === "shipped" 
                ? "bg-blue-100 text-blue-800 border border-blue-200 cursor-default" 
                : "bg-white text-gray-700 border border-gray-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
              } disabled:opacity-50`}
          >
            Mark as Shipped
          </button>

          <button
            type="button"
            disabled={updating || currentStatus === "delivered"}
            onClick={() => updateStatus("delivered")}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all
              ${currentStatus === "delivered" 
                ? "bg-green-100 text-green-800 border border-green-200 cursor-default" 
                : "bg-white text-gray-700 border border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
              } disabled:opacity-50`}
          >
            Mark as Delivered
          </button>
        </div>
      </div>

    </div>
  );
};

export default AdminOrderDetails;